
import { AlertCircle, RefreshCw } from 'lucide-react';

interface MiniAppErrorProps {
  error: string;
  retryCount: number;
  onRetry: () => void;
}

const MiniAppError = ({ error, retryCount, onRetry }: MiniAppErrorProps) => {
  const isNetworkError = error.includes('fetch') || error.includes('network');
  const isBotError = error.includes('bot') || error.includes('Bot');
  const isAuthError = error.includes('not found') || error.includes('verify');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center bg-card rounded-lg p-6 border border-border max-w-md">
        <AlertCircle className="mx-auto mb-4 text-destructive" size={48} />
        <h1 className="text-xl font-bold text-foreground mb-2">Connection Issue</h1>
        
        {isNetworkError && (
          <div className="mb-4">
            <p className="text-muted-foreground mb-2">Network connection problem detected.</p>
            <p className="text-foreground text-sm">Please check your internet connection and try again.</p>
          </div>
        )}
        
        {isBotError && (
          <div className="mb-4">
            <p className="text-muted-foreground mb-2">Bot service temporarily unavailable.</p>
            <p className="text-foreground text-sm">Please try again in a few moments.</p>
          </div>
        )}
        
        {isAuthError && (
          <div className="mb-4">
            <p className="text-muted-foreground mb-2">Account verification needed.</p>
            <p className="text-foreground text-sm">Please verify your BroCode with the bot first:</p>
            <div className="bg-secondary rounded p-2 mt-2">
              <code className="text-foreground text-xs">/verify YOUR-BROCODE</code>
            </div>
          </div>
        )}
        
        {!isNetworkError && !isBotError && !isAuthError && (
          <p className="text-muted-foreground mb-4">{error}</p>
        )}
        
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="bg-primary text-primary-foreground px-6 py-3 rounded font-bold hover:bg-primary/90 flex items-center justify-center mx-auto transition-colors"
            disabled={retryCount >= 5}
          >
            <RefreshCw className="mr-2" size={16} />
            {retryCount >= 5 ? 'Max Retries Reached' : `Retry ${retryCount > 0 ? `(${retryCount})` : ''}`}
          </button>
          
          {retryCount >= 3 && (
            <div className="text-muted-foreground text-xs">
              <p>Having trouble? Try:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Restart the Telegram app</li>
                <li>Check your internet connection</li>
                <li>Contact support if issues persist</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiniAppError;
